import { Container, Section, Stack } from "@humaniti/ui";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { SEO } from "../lib/seo";

const SECTION_HEAD: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  fontSize: "var(--fs-h2)",
  lineHeight: "var(--lh-h2)",
  letterSpacing: "-0.01em",
};

const PARA: CSSProperties = {
  margin: 0,
  fontSize: "var(--fs-body-lg)",
  lineHeight: "var(--lh-body-lg)",
};

const PARA_SECONDARY: CSSProperties = {
  ...PARA,
  color: "var(--fg-secondary)",
};

export const ManifestoPage = () => (
  <>
    <SEO
      title="Manifesto — Datasets belong to the network"
      description="Why Humaniti exists. Frontier AI runs on data labeled by people. Labor deserves the structure of infrastructure — addressable, signed, paid, owned."
      path="/manifesto"
    />
    <Section>
      <Container size="default">
        <Stack gap="2xl">
          <header>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-label)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-secondary)",
              }}
            >
              § Manifesto
            </span>
            <h1
              style={{
                margin: "var(--space-3) 0 0",
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: "var(--fs-display)",
                lineHeight: "var(--lh-display)",
                letterSpacing: "-0.02em",
              }}
            >
              Datasets belong to the network<span style={{ color: "var(--teal)" }}>.</span>
            </h1>
          </header>

          <section>
            <Stack gap="md">
              <h2 style={SECTION_HEAD}>I. The thesis</h2>
              <p style={PARA}>
                Frontier AI runs on data labeled by people. Every model that matters at scale was
                trained on a dataset that someone, somewhere, manually verified. That labor is the
                actual infrastructure of modern AI. We have decided it deserves the structure of
                infrastructure — addressable, signed, paid, owned.
              </p>
              <p style={PARA_SECONDARY}>
                The Network exists because the alternative — invisible labor, opaque vendors,
                datasets that rot inside private buckets — is no longer the only available shape.
                There is another shape. We are building it.
              </p>
            </Stack>
          </section>

          <section>
            <Stack gap="md">
              <h2 style={SECTION_HEAD}>II. The labor problem</h2>
              <p style={PARA}>
                The current vendor model has three quiet failures. The first is opacity. A buyer
                ships data to a vendor and receives labels back. What happened in between — who
                labeled, how it was checked, who got paid, what fraction of the buyer's spend
                reached the person who did the work — is not part of the contract. The buyer trusts.
                The vendor answers. Quality is a status report, not a verifiable property of the
                artifact.
              </p>
              <p style={PARA}>
                The second is gig-work economics. The people who actually label data — Humans, in
                our lexicon, since they are the only Humans the system has — are routed through
                layers of subcontracting that compress hourly rates and eliminate any sense of
                contribution. The work is interchangeable, throwaway, anonymous. The pay reflects
                that framing.
              </p>
              <p style={PARA}>
                The third is the vanishing dataset. Once a model is trained, the labeled corpus
                usually disappears into a private database. It cannot be re-audited. It cannot be
                reused. It cannot be reproduced. The labor that produced it leaves no trace. A field
                that takes data seriously cannot afford a foundation that vanishes the moment it is
                useful. We treat each of these three failures as a design constraint, not a tradeoff
                to be managed. The vendor model has settled into them; the Network refuses them as
                starting conditions.
              </p>
            </Stack>
          </section>

          <section>
            <Stack gap="md">
              <h2 style={SECTION_HEAD}>III. The Humaniti inversion</h2>
              <p style={PARA}>
                We invert all three. Labor is paid directly, per verified label, with the rate
                visible on the page before any work begins. Verification is consensus — N
                independent Humans label the same item, an aggregator weighs each vote by per-skill
                Elo and prior accuracy, a Steward adjudicates disagreement, and gold questions catch
                drift on every batch. The dataset is signed and addressable — every label carries a
                receipt of the Humans who produced and verified it, and the audit chain is
                sha256-linked so that tampering is detectable on a single scan.
              </p>
              <p style={PARA}>
                None of this is exotic. The components are well-understood. Per-skill Elo has
                decades of literature behind it. Dawid-Skene aggregation has been around since the
                seventies. Append-only audit chains are how every serious financial system has
                worked for years. What is new is the commitment to ship them as the default rather
                than as a premium feature. The Network does not have a tier where verification is
                optional. There is one tier. Quality is a protocol property, not a vendor promise.
              </p>
              <p style={PARA_SECONDARY}>
                The same inversion applies to the people who do the work. A Human is not an
                anonymous vendor account. Each Human carries a verifiable accuracy history, an Elo
                per skill, a tier, and a payout record. Verified accuracy translates directly into
                higher per-label rates and access to higher-stakes work. The labor accumulates into
                a portable record of competence — and the per-label receipts make that record
                auditable by anyone, not only by us.
              </p>
            </Stack>
          </section>

          <section>
            <Stack gap="md">
              <h2 style={SECTION_HEAD}>IV. The arc</h2>
              <p style={PARA}>
                Phase 1 is the Network — verified Humans, verified work, direct pay. That is what
                ships first. It is the substrate everything else builds on. Phase 2 is the Record —
                datasets become signed, addressable, permanent records that survive the company that
                produced them. Phase 3 is the Economy — Humans accumulate credits in the work they
                verify, stake them, multiply their pay rate, and qualify for Steward roles. Phase 4
                is the Commons — Humans and Builders govern the Network together; Humaniti the
                company becomes a steward, not an owner.
              </p>
              <p style={PARA_SECONDARY}>
                The through-line is the same in every phase. Labor that produces value should
                accumulate ownership in what it produces, and governance over the rules of its own
                labor. The four phases are the same idea, repeated at four levels of depth.{" "}
                <Link to="/roadmap">Read the full arc →</Link>
              </p>
            </Stack>
          </section>

          <section>
            <Stack gap="md">
              <h2 style={SECTION_HEAD}>V. The invitation</h2>
              <p style={PARA}>
                If you build models, you already pay for labels. The question is whether you pay a
                vendor for an opaque process or a network for a verifiable one. If you label, you
                already do this work. The question is whether you are paid by a subcontractor on
                net-60 terms or by a network on per-verified-label terms with your name on the
                output. Either way, the work is the same. The shape of the work is what we are
                changing.
              </p>
              <p style={PARA}>
                We are not asking anyone to take this on faith. The audit chain is a current shipped
                feature. The consensus pipeline is a current shipped feature. The direct-pay rails
                are a current shipped feature. The pieces of Phase 2, 3, and 4 land on a public
                roadmap with public dates. If we miss a date, the date moves and we say so.
              </p>
            </Stack>
          </section>

          <section>
            <p
              style={{
                margin: 0,
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-label)",
                letterSpacing: "0.06em",
                color: "var(--fg-secondary)",
              }}
            >
              — The Humans of Humaniti
            </p>
          </section>
        </Stack>
      </Container>
    </Section>
  </>
);
